/**
 * Protected Route Component
 * Wraps routes that require authentication
 */

import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * ProtectedRoute component
 * - Shows loading state while checking authentication
 * - Redirects to login if not authenticated
 * - Renders children if authenticated
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <div className="w-10 h-10 rounded-full border-[3px] border-border border-t-primary animate-spin" />
        <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Encode the attempted location as ?returnTo= — LoginPage reads this query
    // param (the same convention the REAUTHENTICATION_REQUIRED reauth flow
    // uses), not router state, to decide where to send the user post-login.
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  // Render protected content
  return <>{children}</>;
}


export default ProtectedRoute;
